
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar, Phone } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ReservationSettingsType } from '@/types/menu';
import ReservationModal from '@/components/ReservationModal';
import TrackPhoneCall from './TrackPhoneCall';

interface ReservationButtonProps {
  restaurantId: string;
  menuType: 'restaurant' | 'website';
  primaryColor?: string;
  restaurantName?: string;
}

export const ReservationButton = ({ 
  restaurantId, 
  menuType, 
  primaryColor, 
  restaurantName = 'Restaurant'
}: ReservationButtonProps) => {
  const [settings, setSettings] = useState<ReservationSettingsType | null>(null);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [restaurantPhone, setRestaurantPhone] = useState<string>(""); 
  const [actualRestaurantName, setActualRestaurantName] = useState<string>(restaurantName);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restaurantExists, setRestaurantExists] = useState(true);
  
  useEffect(() => {
    const fetchReservationSettings = async () => {
      if (!restaurantId) {
        setLoading(false);
        setError("Restaurant ID is missing");
        return;
      }
      
      try {
        setLoading(true);
        console.log('Fetching reservation settings for restaurant:', restaurantId);
        
        // First fetch restaurant data to get phone and actual name
        const { data: restaurantData, error: restaurantError } = await supabase
          .from('restaurants')
          .select('phone, name')
          .eq('id', restaurantId)
          .maybeSingle();
          
        if (restaurantError) {
          // En contexte public (menu en ligne), la requête peut être bloquée par les RLS.
          // On ne doit pas empêcher l'affichage du bouton pour autant.
          console.warn('Unable to fetch restaurant data (will still show reservation button):', restaurantError);
          setRestaurantExists(true);
        } else if (restaurantData) {
          setRestaurantPhone(restaurantData.phone || "");
          setActualRestaurantName(restaurantData.name);
          setRestaurantExists(true);
          console.log('Restaurant data loaded:', restaurantData);
        } else {
          // En contexte public, un SELECT peut retourner 0 ligne à cause des RLS.
          // On ne veut pas cacher le bouton dans ce cas.
          console.warn('Restaurant data not accessible (will still show reservation button).');
          setRestaurantExists(true);
          // On garde restaurantName (prop) comme fallback
        }

        // Then fetch reservation settings even if restaurant doesn't exist (maybe it's a legacy menu)
        const { data, error } = await supabase
          .from('reservation_settings')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .maybeSingle();
          
        if (error) {
          console.error('Error fetching reservation settings:', error);
          setError("Error fetching reservation settings");
        } else if (data) {
          // Create a properly typed settings object
          const typedSettings: ReservationSettingsType = {
            ...data,
            // Ensure primary_color exists
            primary_color: primaryColor || (data as any).primary_color || '#8B5CF6',
            max_covers_per_reservation: (data as any).max_covers_per_reservation ?? 8,
          };
          setSettings(typedSettings);
          console.log('Reservation settings loaded:', typedSettings);
        } else {
          console.log('No reservation settings found for restaurant:', restaurantId);
          // Create default settings object if not found
          setSettings({
            id: '',
            created_at: '',
            updated_at: '',
            restaurant_id: restaurantId,
            online_reservations_enabled: true,
            primary_color: primaryColor || '#8B5CF6',
            tables_for_two: 4,
            tables_for_four: 3,
            tables_for_six: 2,
            tables_for_eight: 1,
            max_tables_per_service: 8,
            max_covers_per_reservation: 8,
            online_reservation_percentage: 70,
            min_reservation_hours: 2,
            max_reservation_days: 30,
            reservation_duration_minutes: 90,
            lunch_service_enabled: true,
            dinner_service_enabled: true
          });
        }
      } catch (error) {
        console.error('Error in fetchReservationSettings:', error);
        setError("Unexpected error loading reservation data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchReservationSettings();
  }, [restaurantId, primaryColor, restaurantName]);
  
  if (loading) {
    return null;
  }
  
  // Note: en menu public, l'accès à la table restaurants peut être bloqué par RLS,
  // on ne doit pas masquer le bouton pour autant.

  // If settings explicitly disable online reservations, just show the phone button if available
  if (settings && settings.online_reservations_enabled === false) {
    return restaurantPhone ? (
      <div className="fixed bottom-4 right-4 z-20">
        <TrackPhoneCall
          restaurantId={restaurantId}
          phone={restaurantPhone}
          className="rounded-full shadow-lg bg-white text-gray-800 hover:bg-gray-100 px-4 py-2 flex items-center justify-center"
        />
      </div>
    ) : null;
  }

  const handleReservationClick = () => {
    console.log('Reservation button clicked, opening modal');
    setIsReservationModalOpen(true);
  };

  const buttonStyle = {
    backgroundColor: primaryColor || (settings?.primary_color || '#8B5CF6'),
  };

  console.log('RestaurantPhone in ReservationButton:', restaurantPhone);

  return (
    <>
      <div className="fixed bottom-4 right-4 z-20 flex flex-col gap-2">
        <Button 
          className="rounded-full shadow-lg" 
          style={buttonStyle}
          onClick={handleReservationClick}
          data-reservation-button="true"
        >
          <Calendar className="mr-2 h-4 w-4" />
          Réserver
        </Button>
        
        {restaurantPhone && restaurantPhone.trim() !== '' && (
          <TrackPhoneCall 
            restaurantId={restaurantId} 
            phone={restaurantPhone}
            className="rounded-full shadow-lg bg-white text-gray-800 hover:bg-gray-100 px-4 py-2 flex items-center justify-center"
          />
        )}
      </div>

      <ReservationModal
        isOpen={isReservationModalOpen}
        onClose={() => setIsReservationModalOpen(false)}
        restaurantId={restaurantId}
        restaurantName={actualRestaurantName}
      />
    </>
  );
};

export default ReservationButton;
